/**
 * useFacetSelection — manages a set of selected facet ids with stable callbacks.
 *
 * Single responsibility: expose the full facet-selection API for a generic
 * string-id facet space:
 *
 *   - `active`     — current selection (immutable view).
 *   - `toggle(id)` — flip an id in the set.
 *   - `add(id)`    — idempotently include an id (returns the same selection
 *                    reference when the id is already present).
 *   - `remove(id)` — idempotently exclude an id (returns the same reference
 *                    when the id was not present).
 *   - `setActive(next)` — replace the entire selection. Identity-preserving:
 *                         returns the previous reference when `next` is the
 *                         same reference or has identical members.
 *   - `clear()`    — empty the selection (identity-preserving when already empty).
 *   - `has(id)`    — membership predicate.
 *
 * Empty selection conventionally means "match all" — caller decides.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useState } from "react";

export interface FacetSelectionState<F extends string> {
  /** Currently active facet ids. */
  readonly active: ReadonlySet<F>;
  /** Toggle a facet on or off. */
  readonly toggle: (id: F) => void;
  /** Add a facet without removing other selections. */
  readonly add: (id: F) => void;
  /** Remove a facet from the active set. */
  readonly remove: (id: F) => void;
  /** Replace the entire active set. */
  readonly setActive: (next: ReadonlySet<F>) => void;
  /** Empty the active set. */
  readonly clear: () => void;
  /** True iff `id` is currently active. */
  readonly has: (id: F) => boolean;
}

/**
 * Manages a multi-select set of facet ids.
 *
 * Empty selection conventionally means "match all" — caller decides.
 *
 * @param initial - Initial active facet ids (defaults to empty).
 */
export function useFacetSelection<F extends string>(initial?: Iterable<F>): FacetSelectionState<F> {
  const [active, setActiveState] = useState<ReadonlySet<F>>(() => new Set<F>(initial));

  const toggle = useCallback((id: F): void => {
    setActiveState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const add = useCallback((id: F): void => {
    setActiveState((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const remove = useCallback((id: F): void => {
    setActiveState((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback((): void => {
    setActiveState((prev) => (prev.size === 0 ? prev : new Set<F>()));
  }, []);

  const setActive = useCallback((next: ReadonlySet<F>): void => {
    setActiveState((prev) => {
      // Identity-preserve when the new selection is the same reference or
      // has identical members. Avoids spurious rerenders/effects in callers
      // that depend on `active` being stable across no-op writes.
      if (prev === next) return prev;
      if (prev.size === next.size) {
        let same = true;
        for (const v of next) {
          if (!prev.has(v)) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }
      return new Set(next);
    });
  }, []);

  const has = useCallback((id: F): boolean => active.has(id), [active]);

  return { active, toggle, add, remove, setActive, clear, has };
}
